import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Container, Typography, Paper, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import dbIcon from './image-icons/dbicon.png';
import connIcon from './image-icons/connIcon.png';
import currIcon from './image-icons/currIcon.png';
import reverseIcon from './image-icons/reverseIcon.png';
import platform1Icon from './image-icons/platform1.png';
import platform2Icon from './image-icons/platform2.png';
import platform3Icon from './image-icons/platform3.png';
import platform4Icon from './image-icons/platform4.png';
import { Check } from '@mui/icons-material';

const FeaturesSection = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const tabs = [
    { label: 'ETL / ELT', value: 0, icon: dbIcon },
    { label: 'Pipelines & Orchestration', value: 1, icon: connIcon },
    { label: 'Telemetry & Logs', value: 2, icon: currIcon },
    { label: 'Data Migration', value: 3, icon: reverseIcon },
  ];

  const startAutoRotation = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const rotateTabs = () => {
      setSelectedTab((prevTab) => {
        const currentTab = typeof prevTab === 'number' ? prevTab : 0;
        const nextTab = (currentTab + 1) % 4;
        return nextTab;
      });
    };

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(rotateTabs, 10000);
    }, 100);
  }, []);

  useEffect(() => {
    startAutoRotation();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [startAutoRotation]);

  const featuresContent = [
    {
      title: 'Flexible, High-Performance Transformations',
      features: [
        'Visual or SQL-based transformation builder',
        'Auto schema detection & field mapping',
        'Reusable transformation components',
        'Real-time preview of data outputs',
        'Versioning & rollback for workflows',
      ],
      image: platform1Icon,
    },
    {
      title: 'Automated, Reliable Data Workflows',
      features: [
        'Smart scheduling with cron, event triggers, & time windows',
        'Dependency resolution & conditional branching',
        'Auto-retry, failover, and alerting',
        'Pipeline monitoring dashboard with logs & metrics',
        'Run history, audit trails, & performance insights',
      ],
      image: platform2Icon,
    },
    {
      title: 'Shape, Optimize & Route Log Data',
      features: [
        'Real-time log filtering & noise reduction',
        'PII masking & rule-based field transformations',
        'High-throughput log ingestion engine',
        'Multi-destination routing (SIEM, S3, warehouse, etc.)',
        'Cost optimization via compression or reduction policies',
      ],
      image: platform3Icon,
    },
    {
      title: 'Zero-Downtime Migrations for SaaS & Databases',
      features: [
        'SaaS API-based migrations with incremental syncs',
        'Database-to-database transfer with CDC',
        'Automated schema mapping & validation',
        'Pre-migration checks & post-migration verification',
        'Cutover workflows for seamless go-live',
      ],
      image: platform4Icon,
    },
  ];

  return (
    <Box
      sx={{
        width: '100vw',
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #1A0F2A 0%, #0F0F1A 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 4, sm: 6, md: 8, lg: 12 } }}>
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 8 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '48px' },
                lineHeight: '100%',
                letterSpacing: '0%',
                textAlign: 'center',
                mb: { xs: 2, sm: 2.5, md: 3 },
                background: 'linear-gradient(180deg, #F6F6F7 0%, #7E808F 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Everything You Need in One Platform
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                lineHeight: '160%',
                letterSpacing: '0px',
                textAlign: 'center',
                color: '#FFFFFFCC',
                maxWidth: '800px',
                mx: 'auto',
                px: { xs: 1, sm: 0 },
              }}
            >
              Enjoy end-to-end capabilities that accelerate engineering, reduce complexity, & keep your data environment resilient & future-ready.
            </Typography>
          </motion.div>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 1.5, md: 2 },
            mb: { xs: 4, sm: 5, md: 6 },
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            px: { xs: 2, sm: 0 },
            overflowX: { xs: 'auto', sm: 'visible' },
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            // Hide scrollbar on mobile
            scrollbarWidth: 'none', // Firefox
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Edge
            },
            // Show scrollbar on larger screens
            '@media (min-width: 600px)': {
              scrollbarWidth: 'auto',
              '&::-webkit-scrollbar': {
                display: 'block',
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
              },
            },
          }}
        >
          {tabs.map((tab) => {
            const isActive = selectedTab === tab.value;
            return (
              <Button
                key={tab.value}
                onClick={() => {
                  setSelectedTab(tab.value);
                  startAutoRotation();
                }}
                sx={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: { xs: '13px', sm: '14px', md: '16px' },
                  lineHeight: { xs: '18px', sm: '19px', md: '20px' },
                  letterSpacing: '-0.15px',
                  textAlign: 'center',
                  color: isActive ? '#FFFFFF' : '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '8px',
                  border: isActive ? '1px solid #8423FA8C' : '1px solid #FFFFFF1A',
                  background: isActive
                    ? 'linear-gradient(135deg, rgba(132, 35, 250, 0.125) 0%, rgba(132, 35, 250, 0.063) 100%)'
                    : '#FFFFFF0D',
                  boxShadow: isActive ? '0px 0px 20px 0px #3B82F630' : 'none',
                  px: { xs: 2, sm: 2.5, md: 3 },
                  py: { xs: 1, sm: 1.25, md: 1.5 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.25, md: 1.5 },
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -6,
                    left: 0,
                    right: 0,
                    height: isActive ? '2px' : '0px',
                    background: isActive ? '#8423FA' : 'transparent',
                    borderRadius: '0 0 8px 8px',
                  },
                  '&:hover': {
                    background: isActive
                      ? 'linear-gradient(135deg, rgba(132, 35, 250, 0.15) 0%, rgba(132, 35, 250, 0.08) 100%)'
                      : 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={tab.icon}
                  alt={tab.label}
                  sx={{
                    width: { xs: 16, sm: 18, md: 20 },
                    height: { xs: 16, sm: 18, md: 20 },
                    imageRendering: 'crisp-edges',
                    WebkitImageRendering: 'crisp-edges',
                  }}
                />
                {tab.label}
              </Button>
            );
          })}
        </Box>

        <AnimatePresence mode="wait">
          <motion.div
            key={selectedTab}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={0}
              sx={{
                background: 'linear-gradient(130.59deg, #B7AEFF -146.23%, #EFF6FF 108.78%)',
                borderRadius: '12px',
                boxShadow: '0px 25px 50px -12px #00000040',
                p: { xs: 3, sm: 4, md: 6 },
                minHeight: { xs: 'auto', md: '400px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 3, sm: 4, md: 6 },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '20px', sm: '22px', md: '24px' },
                      lineHeight: { xs: '28px', sm: '32px', md: '36.4px' },
                      letterSpacing: '0.1px',
                      color: '#0F172B',
                      mb: { xs: 3, sm: 3.5, md: 4 },
                    }}
                  >
                    {featuresContent[selectedTab].title}
                  </Typography>
                  {featuresContent[selectedTab].features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: { xs: 1.5, sm: 1.75, md: 2 },
                        mb: { xs: 2.5, sm: 2.75, md: 3 },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 18, sm: 19, md: 20 },
                          height: { xs: 18, sm: 19, md: 20 },
                          borderRadius: '7px',
                          background: '#7214FF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Check
                          sx={{
                            color: '#ffffff',
                            fontSize: { xs: 14, sm: 15, md: 16 },
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Manrope, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '15px', sm: '16px', md: '18px' },
                          lineHeight: { xs: '140%', sm: '150%', md: '100%' },
                          letterSpacing: '-0.31px',
                          color: '#45556C',
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: { xs: '250px', sm: '280px', md: '100%' },
                  }}
                >
                  <Box
                    component="img"
                    src={featuresContent[selectedTab].image}
                    alt="Platform Diagram"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxWidth: { xs: '100%', sm: '90%', md: '500px', lg: '200px' },
                      maxHeight: { xs: '250px', sm: '280px', md: '300px' },
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default FeaturesSection;

